import React, { useContext, useMemo } from 'react'
import { FormProvider } from 'react-hook-form'
import cn from 'classnames'
import { Button, ButtonStyles, Typography, IconSpinner } from '@grammarly/badger-ui'
import { FormattedMessage } from 'react-intl'

import { TextInput } from '../../../inputs/text-input'
import { Output, SkeletonLines } from './output'
import { TypeComponentTextInput } from '../../../../lib/types/component-text-input'
import {
  FormProvider as UILibFormProvider,
  useFormContext as useUILibFormContext,
} from '../../../ui-lib/form/form-context'
import { replaceSpacesWithDashes } from '../../../../lib/utils/strings'
import { RequiredOrOptional } from '../../../ui-lib/form/form-context'
import { errorStyle } from '../../../ui-lib/form/error-text'
import { FooterBanner } from '../footer-banner'
import { SideBySideErrorOrCounter } from './side-by-side-error-or-counter'
import { AnonymousTextLimitModal } from '../anonymous-text-limit-modal'
import { AnnounceLoading } from '../form'
import { useGoTemplates } from '../go-templates-context'
import { PageContext } from '../../../../contexts/page-context'
import { gnarSpec } from '../../../../lib/tracking/gnar'
import { useBrowserService, useUserService, useExperimentService } from '../../../../lib/hooks'
import { SubmitButtonContent } from '../shared-components/submit-button-content'

import styles from './side-by-side-form.module.scss'
import { useGgoState } from '@lib/services/api/cheetah-provider'

const { C4 } = Typography

interface SideBySideFormProps {
  question: TypeComponentTextInput
}

const FormError = () => {
  const { isAnonymous, isPremium } = useUserService().currentUser
  const { error: ggoError } = useGgoState()

  // Pro users cannot easily get more prompts, so we show a generic error message for now.
  // @todo improve messaging for Pro users.
  let errorMessage = (
    <FormattedMessage
      id="error.generic"
      description="Generic error message"
      defaultMessage="Something went wrong. Please try again later."
    />
  )
  if (ggoError && ggoError.kind) {
    switch (ggoError.kind) {
      case 'promptsLimitExceeded': {
        if (!isPremium) {
          errorMessage = isAnonymous ? (
            <FormattedMessage
              id="goTemplatesTool.sideBySide.formError.promptsLimitExceeded.anonymous"
              description="Go Templates Tool - Side By Side - Form Error - Prompts Limit Exceeded - Anonymous"
              defaultMessage="Sign up for additional access."
            />
          ) : (
            <FormattedMessage
              id="goTemplatesTool.sideBySide.formError.promptsLimitExceeded.existingUser"
              description="Go Templates Tool - Side By Side - Form Error - Prompts Limit Exceeded - Existing User"
              defaultMessage="Upgrade for additional access."
            />
          )
        }
        break
      }
      case 'unsupportedLanguage': {
        errorMessage = (
          <FormattedMessage
            id="goTemplatesTool.sideBySide.formError.unsupportedLanguage"
            description="Go Templates Tool - Side By Side - Form Error - Unsupported Language"
            defaultMessage="Assistance is currently only available in English."
          />
        )
        break
      }
      default: {
        errorMessage = (
          <FormattedMessage
            id="error.generic"
            description="Generic error message"
            defaultMessage="Something went wrong. Please try again later."
          />
        )
      }
    }
  }

  return (
    <C4 as="span" className={errorStyle}>
      {errorMessage}
    </C4>
  )
}

const FormActionRow: React.FC<SideBySideFormProps> = ({ question }) => {
  const { methods, cheetahResultState, submitButtonText, isLoading, loadingButtonText } = useGoTemplates()

  const { setValue } = methods
  const { pageId, slug } = useContext(PageContext)
  const { isMobileOrTablet } = useBrowserService()

  const { isDynamicBannerV2 } = useExperimentService()
  const { isAnonymous } = useUserService().currentUser

  const isDynamicBannerTreatment = useMemo(
    () => isDynamicBannerV2(slug, isMobileOrTablet, isAnonymous),
    [slug, isMobileOrTablet, isAnonymous, isDynamicBannerV2]
  )

  return (
    <div className={cn(styles.buttonContainer, isDynamicBannerTreatment && styles.buttonContainerDynamicBanner)}>
      <Button
        padding="compact"
        className={
          cheetahResultState === 'done'
            ? ButtonStyles.TransparentButtonGreenTextWithBorder24
            : cn(ButtonStyles.GreenButtonWhiteText24, isLoading && styles.submitButtonLoading)
        }
        type="submit"
        iconRight={isLoading && <IconSpinner />}
        disabled={isLoading}
      >
        <SubmitButtonContent
          isLoading={isLoading}
          loadingButtonText={loadingButtonText}
          submitButtonText={submitButtonText}
        />
      </Button>
      {question.fields.sampleText && (
        <Button
          padding="compact"
          className={ButtonStyles.TransparentButtonGrayText}
          type="button"
          status={isLoading ? 'disabled' : 'enabled'}
          onClick={() => {
            gnarSpec.goToolsCtaClick('sampleText', pageId, slug)
            if (question.fields.sampleText) {
              setValue(question.fields.name, question.fields.sampleText, { shouldValidate: true })
            }
          }}
        >
          <FormattedMessage
            id="goTemplatesTool.sideBySide.formActionRow.ctaText"
            description="Go Templates Tool - Side By Side - Form Action Row - CTA Text"
            defaultMessage="Try sample text"
          />
        </Button>
      )}
      <div className={styles.rightContainer}>
        {cheetahResultState === 'error' && !isMobileOrTablet ? (
          <FormError />
        ) : (
          <SideBySideErrorOrCounter structure={question} />
        )}
      </div>
    </div>
  )
}

const Form: React.FC<SideBySideFormProps> = ({ question }) => {
  const {
    methods,
    shouldTriggerWall,
    onFormChangeCapture,
    onFormClickCapture,
    updatePastedPerStep,
    getOnSubmit,
    isLoading,
    onChange,
  } = useGoTemplates()
  const { handleSubmit, watch } = methods
  const { isMobileOrTablet } = useBrowserService()

  const watchQuestionText = watch(question.fields.name, '')
  const hasQuestionText = (watchQuestionText?.length || 0) > 0

  const formId = useUILibFormContext().formId

  const getFormStepTrackingData = (): {
    activeStep: number
    totalSteps: number
    inputName: string
    inputType: 'long text' | 'short text'
    isInputRequired: boolean
  } => {
    const { hasLongformText, name, isRequired } = question.fields
    const inputType = hasLongformText ? 'long text' : 'short text'

    return { activeStep: 1, totalSteps: 1, inputName: name, inputType, isInputRequired: !!isRequired }
  }

  const checkKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      // For longform text, i.e. textarea, pressing enter should move to the next line and not submit the form.
      // @todo Need to support non-longform text (in later work).
      if (question.fields.hasLongformText) {
        e.stopPropagation()
      }
    }
  }
  const onSubmit = getOnSubmit(getFormStepTrackingData(), true)

  const isLabelHidden = !hasQuestionText || isMobileOrTablet

  return (
    <form
      key={question.sys.id}
      id={formId}
      className={styles.form}
      onSubmit={handleSubmit(onSubmit)}
      onKeyDown={checkKeyDown}
      aria-live="polite"
      aria-relevant="additions"
      onChangeCapture={shouldTriggerWall ? onFormChangeCapture : undefined}
      onClickCapture={shouldTriggerWall ? onFormClickCapture : undefined}
      method="post"
    >
      <div className={styles.inputContainer}>
        {question.fields.allowFileUpload || !question.fields.hasLongformText ? null : (
          <TextInput
            structure={question}
            className={cn('font-family-inter', styles.textarea)}
            labelClassName={cn(styles.label, isLabelHidden && styles.labelHidden)}
            onPaste={() => updatePastedPerStep(true)}
            hideErrorText
            hasLongformText={question.fields.hasLongformText}
            onChange={onChange}
          />
        )}
        {isMobileOrTablet && isLoading && (
          <div className={styles.skeletonContainer}>
            <SkeletonLines numLines={4} />
          </div>
        )}
        {isMobileOrTablet && <FormActionRow question={question} />}
      </div>
      {!isMobileOrTablet && <FormActionRow question={question} />}
    </form>
  )
}

export const SideBySideForm: React.FC<SideBySideFormProps> = ({ question }) => {
  const { methods, cheetahResultState, internalName, isWallOpen, isLoading } = useGoTemplates()
  const { isMobileOrTablet } = useBrowserService()

  const formId = `form-side-by-side-${replaceSpacesWithDashes(internalName)}`

  return (
    <div className={cn(styles.toolContainer, isMobileOrTablet && styles.mobile)}>
      <div aria-live="polite" className={styles.formContainer}>
        <FormProvider {...methods}>
          <UILibFormProvider value={{ formId, requiredOrOptionalIndicator: RequiredOrOptional.Optional }}>
            <Form question={question} />
            <AnnounceLoading isLoading={isLoading} />
            <AnonymousTextLimitModal name={question.fields.name} isSignUpWallOpen={isWallOpen} structure={question} />
          </UILibFormProvider>
        </FormProvider>
        {isMobileOrTablet && cheetahResultState === 'error' && <FormError />}
        <Output />
      </div>
      {!isMobileOrTablet && <FooterBanner />}
    </div>
  )
}
